<template>
  <b-card>
    <b-form @reset="resetFilter" v-on:submit.prevent>
      <fieldset :disabled="loading">
        <b-form-group id="user-group" :label="$t('user')" label-for="user">
          <b-form-input id="user" type="text" v-model="filter.user" :state="userState" :disabled=isUserAsActiveProfile(this.activeProfile) v-on:keyup="onKeyup"
                        autofocus/>
          <b-form-invalid-feedback id="user-feedback" v-if="filter.user">
            {{ $t('requireThreeLetters') }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group id="organization-group" :label="$t('school')" label-for="organization">
          <b-form-input id="organization" type="text" v-model="filter.organization" :state="organizationState"
                        :disabled=isOrganizationAsActiveProfile(this.activeProfile) v-on:keyup="onKeyup"/>
          <b-form-invalid-feedback id="organization-feedback" v-if="filter.organization">
            {{ $t('requireThreeLetters') }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group id="source-group" :label="$t('source')" label-for="source">
          <b-form-select size="sm" id="source" v-model="filter.source" :options="userSources" v-on:change="updateUsers">
            <template slot="first">
              <option :value="null">{{ $t('all') }}</option>
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group id="primary-role-group" :label="$t('primaryRole')" label-for="primaryRole">
          <b-form-select size="sm" id="primaryRole" v-model="filter.primaryRole" :options="userPrimaryRoles" v-on:change="updateUsers">
            <template slot="first">
              <option :value="null">{{ $t('all') }}</option>
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group id="additional-role-group" :label="$t('additionalRoles')" label-for="additionalRole">
          <b-form-tags id="additionalRole" v-model="filter.additionalRoles" size="mb" class="mb-2" add-on-change no-outer-focus>
            <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
              <ul v-if="tags.length > 0" class="list-inline d-inline-block mb-2">
                <li v-for="tag in tags" :key="tag" class="list-inline-item">
                  <b-form-tag @remove="removeTag(tag)" :disabled="disabled" variant="info">
                    {{ translateAlternatives('userAdditionalRoles', tag) }}
                  </b-form-tag>
                </li>
              </ul>
              <b-form-select v-bind="inputAttrs" v-on="inputHandlers" :options="userAdditionalRoles">
                <template slot="first">
                  <option disabled value="">{{ $t('addAdditionalRole') }}</option>
                </template>
              </b-form-select>
            </template>
          </b-form-tags>
        </b-form-group>

        <b-form-group id="valid-group" :label="$t('filter')">
          <b-form-checkbox size="sm" v-model="filter.active" name="checkbox" switch inline v-on:change="updateUsers">
            {{ $t('onlyActive') }}
          </b-form-checkbox>
        </b-form-group>

        <b-row>
          <b-col>
            <b-form-select id="perPage" v-model="paging.perPage" :options="paging.perPageAlternatives"/>
          </b-col>
          <b-col>
            <b-button size="sm" class="float-right" v-on:click="resetFilter">{{ $t('reset') }}</b-button>
          </b-col>
        </b-row>
        <b-form-group>
          <!--
          TODO No support for pagination on the server side yet.
          <pagination v-bind:pagination="paging"/>
          -->
        </b-form-group>
      </fieldset>
      <b-row>
        <b-col>
          <b-button type="button" block variant="primary" @click="showCreateUserModal">{{ $t('createUser') }}</b-button>
          <create-user-modal ref="createUserModal"/>
        </b-col>
      </b-row>

      <b-row v-if="leaveOrganization.length > 0" style="margin-top: 1em">
        <b-col>
          <b-button type="button" block variant="danger" @click="showLeaveOrganizationModal">
            {{ $t('leaveOrganization') }}
          </b-button>
          <leave-organization-modal ref="leaveOrganizationModal"/>
        </b-col>
      </b-row>

    </b-form>
  </b-card>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import CreateUserModal from './CreateUserModal'
  import BaseComponent from '../../components/BaseComponent'

  export default {
    name: 'UserFilter',
    extends: BaseComponent,
    components: {
      CreateUserModal,
      LeaveOrganizationModal: () => import('./LeaveOrganizationModal'),
    },
    computed: {
      ...mapGetters('users', ['filter', 'paging', 'loading', 'leaveOrganization']),
      ...mapGetters('sessionStorage', ['activeProfile']),
      ...mapState('users', { filter: state => state.filter }),
      userState () {
        return this.filter.user && this.filter.user.length >= 3
      },
      organizationState () {
        return this.filter.organization && this.filter.organization.length >= 3
      }
    },

    methods: {
      onKeyup (event) {
        if (event.which === 13) {
          this.updateUsers()
        }
      },
      updateUsers () {
        this.$store.dispatch('users/getUsers')
      },
      resetFilter () {
        this.$store.dispatch('users/resetFilter')
        this.updateUsers()
      },
      showCreateUserModal () {
        this.$store.dispatch('users/resetFilter')
        this.$root.$emit('bv::show::modal', 'createUserModal')
      },
      showLeaveOrganizationModal () {
        this.$root.$emit('bv::show::modal', 'leaveOrganizationModal')
      }
    },

    watch: {
      'paging.perPage': function () {
        this.updateUsers()
      },
      'paging.currentPage': function () {
        this.updateUsers()
      },
      'isActiveProfileSet': function () {
        this.updateUsers()
      },
      'filter.additionalRoles': function () {
        this.updateUsers()
      }
    },

    i18n: {
      messages: {
        en: {
          'addAdditionalRole': 'Add additional role',
          'onlyActive': 'Only active'
        },
        nb: {
          'addAdditionalRole': 'Legg til tilleggsrolle',
          'onlyActive': 'Kun aktive'
        }
      }
    }
  }
</script>
